import React from "react";
import { ImageOff, MessageSquareText, Radio, Video } from "lucide-react";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import { cn } from "../../lib/utils";

// O que este contato publicou nas últimas 24 horas.
//
// Fica dentro do painel do contato de propósito: o dono já está decidido, e
// não há feed nenhum — status fora de uma conversa em curso seria rolagem de
// curiosidade, e é justamente o que o recorte de entrada não guarda.
//
// Tempo é conta nossa: o provider não manda expiração, então mostramos "há X"
// e nunca uma contagem regressiva, que fingiria precisão que não temos.

const horaRelativa = (valor) => {
	try {
		return `há ${formatDistanceToNowStrict(parseISO(valor), { locale: ptBR })}`;
	} catch {
		return "";
	}
};

// O nome do WhatsApp acrescenta quando difere do cadastro — e é a única pista
// de quem é quando o contato não tem nome cadastrado.
const nomeDoWhatsapp = (contact, status) => {
	const push = String(status?.[0]?.pushName || "").trim();

	if (!push) return null;

	const cadastrado = String(contact?.name || "").trim();

	// Contato sem nome mostra o número no painel: aí a linha não é divergência,
	// é identificação.
	const semNome = !cadastrado || cadastrado === String(contact?.number || "");

	if (semNome) return push;

	return cadastrado.toLowerCase() === push.toLowerCase() ? null : push;
};

const Item = ({ status }) => {
	const [midiaFalhou, setMidiaFalhou] = React.useState(false);
	const [endereco, setEndereco] = React.useState(null);

	// A imagem NÃO pode sair numa tag <img src> apontando para a rota: o token
	// vive em memória e é posto no cabeçalho pelo interceptor do axios, e a tag
	// não passa por ele — toda miniatura voltaria 401. Então o arquivo é
	// buscado como blob e desenhado a partir de um endereço local.
	React.useEffect(() => {
		if (!status.temMidia) return undefined;

		let local = null;
		let valeAinda = true;

		api
			.get(`/status-recebidos/${status.id}/midia`, { responseType: "blob" })
			.then(({ data }) => {
				if (!valeAinda) return;
				local = URL.createObjectURL(data);
				setEndereco(local);
			})
			.catch(() => setMidiaFalhou(true));

		return () => {
			valeAinda = false;
			if (local) URL.revokeObjectURL(local);
		};
	}, [status.id, status.temMidia]);

	const ehImagem =
		status.temMidia && /^image\//.test(status.mediaMimetype || "");
	const ehVideo =
		status.type === "video" || /^video\//.test(status.mediaMimetype || "");

	return (
		<div className="flex gap-2.5">
			<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
				{ehImagem && !midiaFalhou && endereco ? (
					<img
						src={endereco}
						alt={status.body || "Status publicado pelo contato"}
						className="h-full w-full max-w-full object-cover"
						// A referência é guardada, o arquivo não: dentro das 24h a
						// origem pode sumir antes do status. Sem retry — o arquivo não
						// volta, e insistir só piscaria a tela.
						onError={() => setMidiaFalhou(true)}
					/>
				) : (
					<>
						{ehVideo && <Video className="h-4 w-4 text-muted-foreground" />}
						{!ehVideo && (midiaFalhou || status.temMidia) && (
							<ImageOff className="h-4 w-4 text-muted-foreground" />
						)}
						{!ehVideo && !status.temMidia && (
							<MessageSquareText className="h-4 w-4 text-muted-foreground" />
						)}
					</>
				)}
			</div>
			<div className="min-w-0 flex-1">
				{ehVideo && !status.body && <p className="text-sm">Publicou um vídeo</p>}
				{midiaFalhou && !ehVideo && (
					<p className="text-xs text-muted-foreground">Imagem não disponível</p>
				)}
				{status.body && <p className="line-clamp-3 text-sm">{status.body}</p>}
				<p className="pt-0.5 text-xs text-muted-foreground">
					{horaRelativa(status.publicadoEm)}
				</p>
			</div>
		</div>
	);
};

const StatusDoContato = ({ contact, status, className }) => {
	// Cartão vazio permanente seria espaço morto no painel de todo contato: sem
	// status válido, ele simplesmente não existe. Quem sinaliza que existe algo
	// é o anel no avatar da conversa.
	if (!status?.length) return null;

	const push = nomeDoWhatsapp(contact, status);

	return (
		<div className={cn("rounded-xl border bg-card p-4", className)}>
			<p className="mb-2 flex items-center gap-2 text-sm font-semibold">
				<Radio className="h-4 w-4 text-muted-foreground" />
				Status recentes
			</p>

			{push && (
				<p className="mb-2 text-xs text-muted-foreground">
					No WhatsApp: &quot;{push}&quot;
				</p>
			)}

			<div className="space-y-3">
				{status.map((item) => (
					<Item key={item.id} status={item} />
				))}
			</div>

			{/* Contrato escrito, não dedução: se algum dia um caminho passar a
			    marcar visualização, esta linha obriga a discussão antes. */}
			<p className="pt-3 text-[11px] text-muted-foreground">
				Ver aqui não marca visualização para o contato.
			</p>
		</div>
	);
};

export default StatusDoContato;
